/**
 * Pivô PURO da base de respostas de uma etapa → matriz de planilha
 * (1 candidato por linha, 1 pergunta por coluna). Sem ExcelJS, sem DOM, sem
 * fetch — testável isolado. A geração do XLSX + download vive em
 * `export-responses.ts`.
 */

export interface StageResponsesExport {
  questions: { key: string; title: string }[];
  candidates: {
    userId: string;
    name: string | null;
    email: string | null;
    /** ISO 8601 (instante) da conclusão; null se não concluiu. */
    completedAt: string | null;
    /** Score (assessments); null para formulários. */
    score: string | null;
    /** `response_snapshot` cru (SurveyJS `name → valor`). */
    answers: Record<string, unknown>;
  }[];
}

export interface ResponsesMatrix {
  header: string[];
  rows: string[][];
}

/**
 * Uma etapa expõe o ícone "Exportar base de respostas" quando é **survey-backed**
 * — as respostas dos candidatos viram planilha (1 candidato/linha, 1 pergunta/
 * coluna). O sinal é a presença de um **catálogo de survey vinculado**:
 *  - `formId` — formulário autorado pelo consultor;
 *  - `testId` — avaliação/assessment (Raio-X Comportamental, Raio-X de Carreira,
 *    Fit Cultural); é o `assessmentId` do BFF, renomeado no container
 *    (`JobFlowPage.tsx` → `toComponentPhases`).
 *
 * Ambos são servidos pela MESMA rota Core `GET /jobs/:jobId/modules/:moduleId/
 * responses`, então o export funciona idêntico.
 *
 * **Por que não gatear pelo token de tipo** (`stage.type === 'form' | 'teste'`,
 * como era antes): o BFF rebaixa para `form` todo `core.module_type` fora das 7
 * keys do `STAGE_TYPE_KEY_TO_TOKEN` (`?? 'form'` em `job-row-mapper.ts`) — hoje
 * 18 dos 25 tipos. O ícone aparecia nessas etapas e o export baixava planilha
 * vazia. E o tipo nem determina a resposta: entre os 18 há tipos que às vezes
 * têm survey (`learning-map`, `fit-cultural`) e tipos que nunca têm
 * (`personal-data`, `profile-photo`). Só `formId`/`assessmentId` determinam —
 * é exatamente o que o Core lê para resolver o survey
 * (`surveyId = formSurveyId ?? assessmentSurveyId`, ver
 * `module-responses-export.drizzle.adapter.ts`).
 */
export function canExportStageResponses(stage: {
  formId?: string | null;
  testId?: string | null;
  /**
   * Aceito e deliberadamente IGNORADO. A etapa Fator H guarda a referência em
   * `module.config` (via `readExternalConfig`), não em `assessmentId`, então
   * não há survey para tabular — e o Core devolveria `questions: []`.
   */
  fatorHId?: string | null;
}): boolean {
  return Boolean(stage.formId) || Boolean(stage.testId);
}

/** Motivo pelo qual uma base de respostas não pode virar planilha. */
export type StageResponsesExportBlocker = 'no-survey' | 'no-candidates';

/**
 * Classifica a resposta do Core ANTES de gerar o XLSX. Puro de propósito — a
 * decisão de abortar precisa ser testável sem ExcelJS, DOM ou fetch.
 *
 * `no-survey` cobre o caso em que a rota responde **200 com `questions: []`**:
 * o módulo não tem `form_id` nem `assessment_id`, então `surveyData` é null e
 * `extractSurveyQuestions(null)` devolve `[]` (Core
 * `module-responses-export.drizzle.adapter.ts`). Sem esta checagem o
 * `buildResponsesMatrix` gerava só as 4 colunas fixas (ID/Nome/E-mail/Concluído
 * em), o download acontecia e o toast dizia "Base de respostas exportada." — o
 * consultor recebia uma planilha silenciosamente vazia.
 *
 * Vale como rede de segurança MESMO com o gate de `canExportStageResponses`:
 * uma etapa pode ter `formId` e ainda assim o survey não resolver (versão
 * ausente em `survey` — o adapter casa `id` + `version`).
 */
export function stageResponsesExportBlocker(
  data: StageResponsesExport
): StageResponsesExportBlocker | null {
  if (!data?.questions?.length) return 'no-survey';
  if (!data.candidates?.length) return 'no-candidates';
  return null;
}

// `sanitizeXmlText`, `formatAnswerCell` e `formatCompletedAt` mudaram para
// `@/shared/xlsx` quando a exportação por PROGRAMA (épico EU-561) passou a
// precisar dos mesmos utilitários. Re-exportados aqui para não quebrar quem
// já importava deste módulo (inclusive o teste pinado da regressão do XLSX).
import { formatAnswerCell, formatCompletedAt, sanitizeXmlText } from '@/shared/xlsx';

export { formatAnswerCell, formatCompletedAt, sanitizeXmlText };

/**
 * Monta o cabeçalho + as linhas da planilha. Colunas fixas de identidade
 * (Nome, E-mail) → uma coluna por pergunta (na ordem do survey) → Concluído em
 * → Pontuação (só quando ao menos um candidato tem score). Resposta ausente
 * vira célula vazia.
 */
export function buildResponsesMatrix(data: StageResponsesExport): ResponsesMatrix {
  const questions = data.questions ?? [];
  const hasScore = data.candidates.some((c) => c.score !== null && c.score !== undefined);

  const header = [
    'ID do usuário',
    'Nome',
    'E-mail',
    ...questions.map((q) => (q.title && q.title.trim() ? q.title : q.key)),
    'Concluído em',
    ...(hasScore ? ['Pontuação'] : []),
  ];

  const rows = data.candidates.map((c) => [
    c.userId,
    c.name ?? '',
    c.email ?? '',
    ...questions.map((q) => formatAnswerCell(c.answers?.[q.key])),
    formatCompletedAt(c.completedAt),
    ...(hasScore ? [c.score ?? ''] : []),
  ]);

  // Ponto de estrangulamento da higienização: TUDO que vira célula passa por
  // aqui, então nenhum caractere proibido em XML escapa para o XLSX — venha
  // ele da resposta, do título do survey ou do nome/e-mail do candidato.
  // Ver `sanitizeXmlText` para o porquê.
  return {
    header: header.map(sanitizeXmlText),
    rows: rows.map((row) => row.map(sanitizeXmlText)),
  };
}
